import { CdkDrag } from '@angular/cdk/drag-drop';
import { Component, computed, input, output, signal, viewChild, viewChildren } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidOption, TOption } from '../../components/rid-option/rid-option';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';

@Component({
  selector: 'rid-categorise',
  imports: [
    CdkDrag,
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidOverlayFeedback,
    RidMechanicTitle,
    RidOption,
  ],
  templateUrl: './rid-categorise.html',
  host: {
    class: 'block',
  },
})
export class RidCategorise {
  name = input.required<string>();
  questions =
    input.required<{ id: string; item: TOption; assignment: string; options: [TOption, TOption, TOption]; answer: 0 | 1 | 2 }[]>();

  draggable = viewChild.required<CdkDrag>('draggable');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');
  item = viewChild.required<RidOption>('item');
  options = viewChildren<RidOption>('options');

  index = signal(0);
  question = computed(() => this.questions()[this.index()]);
  progress = signal(0);
  isDragging = signal(false);
  isEvaluating = signal(false);
  isCorrect = signal(false);
  target = signal<undefined | number>(undefined);

  finished = output<void>();

  onItemPoppedIn() {
    this.item().present();
  }

  onDragStarted() {
    this.isDragging.set(true);
  }

  // NOTE: this is needed for touch support, because the native touchenter and touchleave events don't work.
  onDragMoved() {
    if (!this.isDragging()) return;

    // Get the center position of the dragged element
    const dragElement = this.draggable().element.nativeElement;
    const rect = dragElement.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    // Find what element is under the drag center
    const elementBelow = document.elementFromPoint(centerX, centerY);
    if (!elementBelow) return;

    // Find the closest card element with data-option-index
    const cardElement = elementBelow.closest('[data-option-index]');
    const newIndex = cardElement ? Number(cardElement.getAttribute('data-option-index')) : undefined;
    const target = this.target();

    if (target !== undefined && newIndex !== target) {
      this.onCardMouseLeave(target);
    }

    if (newIndex !== undefined && newIndex !== target) {
      this.onCardMouseEnter(newIndex);
    }
  }

  onCardMouseEnter(index: number) {
    if (!this.isDragging()) return;

    this.target.set(index);
    const option = this.options()[index];
    option.present();
  }

  onCardMouseLeave(index: number) {
    if (this.isDragging() && this.target() === index) {
      this.target.set(undefined);
    }
  }

  async onDragReleased() {
    this.isDragging.set(false);
    if (this.target() !== undefined) {
      this.isEvaluating.set(true);
      const isCorrect = this.target() === this.question().answer;

      if (isCorrect) {
        await this.markCorrect();
      } else {
        await this.markIncorrect();
      }
    } else {
      await this.moveToOrigin();
    }

    this.target.set(undefined);
    this.isEvaluating.set(false);
  }

  async markCorrect() {
    this.isCorrect.set(true);
    this.progress.set(this.progress() + 1);
    await this.overlayFeedback().showCorrect();

    this.next();
  }

  async markIncorrect() {
    this.overlayFeedback().showIncorrect();
    this.draggable().element.nativeElement.style.transition = `opacity 400ms ease`;
    this.draggable().element.nativeElement.style.opacity = '0.5';
    this.moveToOrigin();

    await delay(750);
    this.draggable().element.nativeElement.style.transition = `opacity 400ms ease`;
    this.draggable().element.nativeElement.style.opacity = '1';
  }

  async moveToOrigin() {
    const duration = 300;
    this.draggable().element.nativeElement.style.transition = `transform ${duration}ms var(--ease-out-back)`;
    this.draggable().element.nativeElement.style.pointerEvents = 'none';
    this.draggable().setFreeDragPosition({ x: 0, y: 0 });

    await delay(duration);

    this.draggable().element.nativeElement.style.transition = '';
    this.draggable().element.nativeElement.style.pointerEvents = '';
  }

  next() {
    this.isCorrect.set(false);

    if (this.index() === this.questions().length - 1) {
      this.finished.emit();
    } else {
      this.index.set(this.index() + 1);
    }
  }
}
